import apiClient from './api.service';

// =====================================================
// STAGE MASTER TYPES
// =====================================================
export interface WorkOrderStageMaster {
  id: string;
  company_id: string;
  stage_code: string;
  stage_name: string;
  stage_description?: string;
  sequence_number: number;
  is_active: boolean;
  created_by?: string;
  created_at: string;
  updated_at: string;
}

// =====================================================
// WORK ORDER STAGE TYPES
// =====================================================
export interface WorkOrderStage {
  id: string;
  work_order_id: string;
  work_order_line_item_id?: string;
  stage_master_id: string;
  stage_code: string;
  stage_name: string;
  description?: string;
  sequence_number: number;
  status: 'Pending' | 'In Progress' | 'Completed' | 'On Hold' | 'Skipped' | 'Paused' | 'Inspection Pending' | 'Inspection Rejected';
  started_date?: string;
  completed_date?: string;
  completed_by?: string;
  notes?: string;
  boq_line_item_operation_id?: string | null;
  /** 1-based unit within WO line; null = legacy single row per op */
  production_unit_number?: number | null;
  // Timeline Planning
  planned_start_date?: string;
  planned_end_date?: string;
  estimated_hours?: number;
  // Resource Assignment
  /** Operator (user running the op); same as backend `assigned_to_user_id`. */
  assigned_to_user_id?: string;
  inspector_user_id?: string | null;
  verifier_user_id?: string | null;
  workstation_id?: string;
  shift_id?: string;
  // Tracking
  actual_hours?: number;
  is_critical_path?: boolean;
  // Priority & Dependencies
  priority?: 'Critical' | 'High' | 'Medium' | 'Low';
  wait_for_previous?: boolean;
  // Inspection gate
  inspection_required?: boolean;
  inspection_status?: string | null;
  created_at: string;
  updated_at: string;
  stageMaster?: WorkOrderStageMaster;
  dimensions?: WorkOrderStageDimension[];
}

export interface WorkOrderStageDimension {
  id: string;
  work_order_stage_id: string;
  work_order_line_item_id: string;
  dimension_name: string;
  dimension_code?: string;
  /** Free-text REQUIRED DIMENSION/TOLERANCE when set, UI shows this instead of numeric tolerance. */
  specification_text?: string | null;
  nominal_value: number;
  unit: string;
  tolerance_type: 'Plus-Minus' | 'Min-Max' | 'Nominal' | 'Go-NoGo';
  tolerance_plus?: number;
  tolerance_minus?: number;
  min_value?: number;
  max_value?: number;
  is_mandatory: boolean;
  sequence_number: number;
  created_at: string;
  updated_at: string;
}

// =====================================================
// INITIAL INSPECTION REQUEST (IIR) TYPES
// =====================================================
export interface InitialInspectionRequest {
  id: string;
  company_id: string;
  work_order_id: string;
  iir_number: string;
  status: 'Pending' | 'In Progress' | 'Approved' | 'Rejected' | 'Cancelled';
  requested_date: string;
  requested_by: string;
  inspector_id?: string;
  inspection_date?: string;
  approved_date?: string;
  approved_by?: string;
  rejection_reason?: string;
  findings?: string;
  observations?: string;
  report_date?: string | null;
  report_title?: string | null;
  report_subtitle?: string | null;
  report_no?: string | null;
  job_no?: string | null;
  wr_no?: string | null;
  client_ref_1?: string | null;
  client_ref_2?: string | null;
  po_no?: string | null;
  make?: string | null;
  gate_pass_no?: string | null;
  qty_no?: string | null;
  customer_name?: string | null;
  work_description?: string | null;
  equipment_name?: string | null;
  scope_of_work_text?: string | null;
  remarks?: string | null;
  findings_rows?: InitialInspectionFindingsRow[] | null;
  parts_replace_rows?: InitialInspectionPartLineRow[] | null;
  parts_repair_rows?: InitialInspectionPartLineRow[] | null;
  prepared_by_name?: string | null;
  prepared_by_print_name?: string | null;
  prepared_by_designation?: string | null;
  approved_by_name?: string | null;
  approved_by_print_name?: string | null;
  approved_by_designation?: string | null;
  supporting_documents?: string[];
  photos?: string[];
  created_at: string;
  updated_at: string;
  workOrder?: any;
  inspector?: any;
  requester?: any;
  approver?: any;
}

export interface InitialInspectionFindingsRow {
  s_no?: string;
  finding?: string;
  action_to_be_taken?: string;
}

export interface InitialInspectionPartLineRow {
  s_no?: string;
  part?: string;
  qty?: string;
  uom?: string;
}

// =====================================================
// STAGE COMPLETION INSPECTION (SCI) TYPES
// =====================================================
export interface StageCompletionInspection {
  id: string;
  company_id: string;
  work_order_id: string;
  work_order_stage_id: string;
  work_order_line_item_id: string;
  inspection_number: string;
  status: 'Pending' | 'In Progress' | 'Pending Approval' | 'Completed' | 'Failed' | 'On Hold';
  result: 'Pass' | 'Fail' | 'Conditional' | 'Pending';
  approval_instance_id?: string | null;
  pending_result?: 'Pass' | 'Fail' | 'Conditional' | null;
  inspector_id: string;
  inspection_date?: string;
  findings?: string;
  observations?: string;
  supporting_documents?: string[];
  photos?: string[];
  report_metadata?: Record<string, unknown>;
  rejection_reason?: string | null;
  rework_notes?: string | null;
  inspection_cycle?: number;
  // Unified inspection fields
  inspection_source?: string;
  quality_plan_id?: string | null;
  quality_plan_item_id?: string | null;
  inspection_point_id?: string | null;
  project_id?: string | null;
  inspection_category?: string;
  scheduled_date?: string | null;
  actual_date?: string | null;
  witness_required?: boolean;
  witness_party?: string | null;
  witness_name?: string | null;
  witness_signature?: any;
  witness_date?: string | null;
  is_hold_point?: boolean;
  hold_point_released?: boolean;
  released_by?: string | null;
  released_at?: string | null;
  ncr_generated?: boolean;
  ncr_id?: string | null;
  ncr?: { id: string; ncr_number: string; status?: string } | null;
  has_dimensions?: boolean;
  created_at: string;
  updated_at: string;
  measurements?: StageInspectionMeasurement[];
  checkpoints?: InspectionCheckpoint[];
  attachments?: InspectionAttachment[];
  /** Relations from API */
  workOrder?: { work_order_number?: string; id?: string };
  workOrderStage?: {
    stage_code?: string;
    stage_name?: string;
    id?: string;
    production_unit_number?: number | null;
  };
  inspector?: { name?: string; email?: string; id?: string };
  workOrderLineItem?: {
    id?: string;
    line_number?: number;
    item_code?: string | null;
    item_description?: string | null;
    revision?: string | null;
    drawing_reference_number?: string | null;
    part_number?: string | null;
    part_description?: string | null;
    heat_number?: string | null;
    planned_quantity?: number | null;
    uom?: string | null;
  };
}

export interface InspectionCheckpoint {
  id: string;
  company_id: string;
  inspection_id: string;
  checkpoint_type: string;
  description?: string | null;
  acceptance_criteria?: string | null;
  result: 'Pass' | 'Fail' | 'Conditional' | 'N/A' | 'Pending';
  remarks?: string | null;
  checked_by?: string | null;
  checked_at?: string | null;
  sequence_number: number;
  created_at: string;
  updated_at: string;
  checker?: { name?: string; email?: string };
}

export interface InspectionAttachment {
  id: string;
  company_id: string;
  inspection_id: string;
  file_name: string;
  file_path: string;
  file_type?: string | null;
  file_size?: number | null;
  category: string;
  remarks?: string | null;
  uploaded_by: string;
  created_at: string;
  uploader?: { name?: string; email?: string };
}

export interface StageInspectionMeasurement {
  id: string;
  stage_completion_inspection_id: string;
  stage_dimension_id: string;
  unit_sequence: number;
  measured_value?: number | null;
  actual_text?: string | null;
  result: 'Pass' | 'Fail' | 'Conditional';
  deviation?: number;
  deviation_percentage?: number;
  notes?: string;
  measured_by: string;
  measured_at: string;
  created_at: string;
  updated_at: string;
  stageDimension?: WorkOrderStageDimension;
}

// =====================================================
// FINAL INSPECTION REQUEST (FIR) TYPES
// =====================================================
export interface FirRequestAttachment {
  id: string;
  fir_request_id: string;
  file_name: string;
  file_type: string;
  file_size?: number | null;
  file_url: string;
  uploaded_by: string;
  uploaded_at: string;
}

export interface FinalInspectionRequest {
  id: string;
  company_id: string;
  work_order_id: string;
  /** One FIR per WO line; null = legacy WO-level FIR */
  work_order_line_item_id?: string | null;
  fir_number: string;
  status: 'Pending' | 'In Progress' | 'Approved' | 'Rejected' | 'Partial';
  requested_date: string;
  requested_by: string;
  inspector_id?: string;
  inspection_date?: string;
  approved_date?: string;
  approved_by?: string;
  approved_quantity: number;
  rejected_quantity: number;
  rejection_reason?: string;
  stage_summary?: any;
  supporting_documents?: string[];
  photos?: string[];
  inspection_criteria?: string | null;
  inspection_equipment?: Array<{
    asset_id: string;
    asset_code: string;
    asset_name: string;
    sort_order: number;
  }>;
  created_at: string;
  updated_at: string;
  lineItemResults?: FIRLineItemResult[];
  attachments?: FirRequestAttachment[];
  /** Populated when API joins user */
  requester?: { name?: string };
  inspector_name?: string;
  inspector?: { name?: string };
  approver?: { name?: string };
  /** Populated when API joins work order */
  workOrder?: {
    work_order_number?: string;
    planned_quantity?: number;
    sales_order_id?: string | null;
    salesOrder?: {
      sales_order_number?: string;
      customer_name?: string;
      customer_po_number?: string | null;
      project_name?: string | null;
    } | null;
  };
  workOrderLineItem?: {
    id?: string;
    line_number?: number;
    item_code?: string | null;
    item_description?: string | null;
    revision?: string | null;
    drawing_reference_number?: string | null;
    part_number?: string | null;
    part_description?: string | null;
    heat_number?: string | null;
    planned_quantity?: number;
    uom?: string | null;
  };
}

export interface FIRLineItemResult {
  id: string;
  fir_id: string;
  work_order_line_item_id: string;
  approved_quantity: number;
  rejected_quantity: number;
  rejection_reason?: string;
  ncr_id?: string;
  revised_work_order_id?: string;
  created_at: string;
  updated_at: string;
  /** Populated when API joins work order line item */
  workOrderLineItem?: {
    line_number?: number;
    item_code?: string | null;
    item_description?: string;
    revision?: string | null;
    planned_quantity?: number;
  };
}

// =====================================================
// INSPECTION RELEASE NOTE (IRN) TYPES
// =====================================================

/** Customer master data joined on delivery note (inspector view). */
export interface IrnCustomerSnapshot {
  customer_code?: string;
  customer_name?: string;
  contact_person?: string | null;
  email?: string | null;
  phone?: string | null;
  mobile?: string | null;
  billing_address_line1?: string | null;
  billing_address_line2?: string | null;
  billing_city?: string | null;
  billing_state?: string | null;
  billing_country?: string | null;
  billing_postal_code?: string | null;
  shipping_address_line1?: string | null;
  shipping_address_line2?: string | null;
  shipping_city?: string | null;
  shipping_state?: string | null;
  shipping_country?: string | null;
  shipping_postal_code?: string | null;
}

export interface IrnDeliveryNoteItemSnapshot {
  id?: string;
  line_number?: number;
  item_code?: string | null;
  item_description?: string | null;
  delivered_quantity?: number;
  uom?: string | null;
  serial_numbers?: string | null;
  batch_numbers?: string | null;
  hsn_code?: string | null;
  notes?: string | null;
}

export interface IrnDeliveryNoteSnapshot {
  id?: string;
  delivery_note_number?: string;
  delivery_date?: string;
  delivery_time?: string | null;
  delivery_address?: string | null;
  customer_po_number?: string | null;
  delivery_status?: string;
  shipping_method?: string | null;
  tracking_number?: string | null;
  vehicle_number?: string | null;
  driver_name?: string | null;
  driver_phone?: string | null;
  special_instructions?: string | null;
  notes?: string | null;
  salesOrder?: { id?: string; sales_order_number?: string } | null;
  customer?: IrnCustomerSnapshot | null;
  items?: IrnDeliveryNoteItemSnapshot[];
  workOrder?: { id?: string; work_order_number?: string } | null;
}

export interface IrnFirLineResultSnapshot {
  id?: string;
  approved_quantity?: number;
  rejected_quantity?: number;
  rejection_reason?: string | null;
  workOrderLineItem?: {
    line_number?: number;
    item_code?: string | null;
    item_description?: string | null;
    planned_quantity?: number;
  } | null;
}

export interface IrnFirSnapshot {
  id?: string;
  fir_number?: string;
  status?: string;
  inspection_date?: string | null;
  requested_date?: string | null;
  approved_quantity?: number;
  rejected_quantity?: number;
  rejection_reason?: string | null;
  workOrderLineItem?: {
    line_number?: number;
    item_code?: string | null;
    item_description?: string | null;
    planned_quantity?: number;
  } | null;
  lineItemResults?: IrnFirLineResultSnapshot[];
}

export interface InspectionReleaseNote {
  id: string;
  company_id: string;
  delivery_note_id?: string | null;
  work_order_id?: string | null;
  fir_id?: string | null;
  irn_number: string;
  status: 'Draft' | 'Pending Approval' | 'Approved' | 'Rejected' | 'Cancelled';
  release_quantity: number;
  release_date?: string;
  released_by?: string;
  approved_date?: string;
  approved_by?: string;
  rejection_reason?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
  releaser?: { name?: string };
  approver?: { name?: string };
  workOrder?: { id?: string; work_order_number?: string } | null;
  deliveryNote?: IrnDeliveryNoteSnapshot | null;
  fir?: IrnFirSnapshot | null;
}

// =====================================================
// STAGE MASTER SERVICE
// =====================================================
export const stageMasterService = {
  create: (data: Partial<WorkOrderStageMaster>) =>
    apiClient.post('/manufacturing/stage-masters', data),

  getAll: (includeInactive?: boolean) =>
    apiClient.get(`/manufacturing/stage-masters?includeInactive=${includeInactive || false}`),

  getBySequence: () =>
    apiClient.get('/manufacturing/stage-masters/sequence'),

  getById: (id: string) =>
    apiClient.get(`/manufacturing/stage-masters/${id}`),

  update: (id: string, data: Partial<WorkOrderStageMaster>) =>
    apiClient.put(`/manufacturing/stage-masters/${id}`, data),

  delete: (id: string) =>
    apiClient.delete(`/manufacturing/stage-masters/${id}`),
};

// =====================================================
// WORK ORDER STAGE SERVICE
// =====================================================
export const workOrderStageService = {
  // Manual stage creation (user selects from stage masters)
  createStage: (
    workOrderId: string,
    stageData: {
      stage_master_id: string;
      sequence_number: number;
      work_order_line_item_id?: string;
      assigned_to_user_id?: string | null;
      inspector_user_id?: string | null;
      verifier_user_id?: string | null;
      workstation_id?: string | null;
      shift_id?: string | null;
      planned_start_date?: unknown;
      planned_end_date?: unknown;
      estimated_hours?: number;
      priority?: string;
      is_critical_path?: boolean;
      wait_for_previous?: boolean;
      inspection_required?: boolean;
      notes?: string;
      description?: string;
    }
  ) =>
    apiClient.post(`/manufacturing/work-orders/${workOrderId}/stages`, stageData),

  // Automatic stage creation from stage masters
  createStages: (workOrderId: string, lineItemIds?: string[], inspection_required?: boolean) =>
    apiClient.post(`/manufacturing/work-orders/${workOrderId}/stages/from-masters`, {
      lineItemIds,
      inspection_required,
    }),

  getStages: (workOrderId: string, lineItemId?: string) => {
    const params = lineItemId ? `?lineItemId=${lineItemId}` : '';
    return apiClient.get(`/manufacturing/work-orders/${workOrderId}/stages${params}`);
  },

  getStagesByLineItem: (workOrderId: string, lineItemId: string) =>
    apiClient.get(`/manufacturing/work-orders/${workOrderId}/stages/line-item/${lineItemId}`),

  getCurrentStage: (workOrderId: string) =>
    apiClient.get(`/manufacturing/work-orders/${workOrderId}/current-stage`),

  getById: (id: string) =>
    apiClient.get(`/manufacturing/stages/${id}`),

  updateStage: (id: string, data: any) =>
    apiClient.put(`/manufacturing/stages/${id}`, data),

  startStage: (id: string, notes?: string) =>
    apiClient.post(`/manufacturing/stages/${id}/start`, { notes }),

  startOutsourcedWithPr: (
    id: string,
    body: {
      pr_date: string;
      required_date: string;
      department_id: string;
      notes?: string;
      lines: Array<{
        item_id: string;
        requested_quantity: number;
        uom: string;
        estimated_unit_price?: number;
        specifications?: string;
        notes?: string;
      }>;
    }
  ) => apiClient.post(`/manufacturing/stages/${id}/start-outsourced`, body),

  revertStageToPending: (id: string, notes?: string) =>
    apiClient.post(`/manufacturing/stages/${id}/revert-to-pending`, { notes }),

  requestInspection: (id: string, notes?: string) =>
    apiClient.post(`/manufacturing/stages/${id}/request-inspection`, { notes }),

  completeStage: (id: string, notes?: string) =>
    apiClient.post(`/manufacturing/stages/${id}/complete`, { notes }),

  pauseStage: (id: string, notes?: string) =>
    apiClient.post(`/manufacturing/stages/${id}/pause`, { notes }),

  holdStage: (id: string, notes?: string) =>
    apiClient.post(`/manufacturing/stages/${id}/hold`, { notes }),

  resumeStage: (id: string, notes?: string) =>
    apiClient.post(`/manufacturing/stages/${id}/resume`, { notes }),

  updateStatus: (id: string, status: string, notes?: string) =>
    apiClient.put(`/manufacturing/stages/${id}/status`, { status, notes }),

  addDimension: (stageId: string, data: Partial<WorkOrderStageDimension>) =>
    apiClient.post(`/manufacturing/stages/${stageId}/dimensions`, data),

  getDimensions: (stageId: string) =>
    apiClient.get(`/manufacturing/stages/${stageId}/dimensions`),

  updateDimension: (dimensionId: string, data: Partial<WorkOrderStageDimension>) =>
    apiClient.put(`/manufacturing/dimensions/${dimensionId}`, data),

  deleteDimension: (dimensionId: string) =>
    apiClient.delete(`/manufacturing/dimensions/${dimensionId}`),
};

// =====================================================
// INITIAL INSPECTION REQUEST (IIR) SERVICE
// =====================================================
export const iirService = {
  createForWorkOrder: (workOrderId: string) =>
    apiClient.post(`/quality/iirs/work-order/${workOrderId}`),

  getAll: (filters?: { status?: string; workOrderId?: string }) => {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.workOrderId) params.append('workOrderId', filters.workOrderId);
    return apiClient.get(`/quality/iirs?${params.toString()}`);
  },

  getById: (id: string) =>
    apiClient.get(`/quality/iirs/${id}`),

  getByWorkOrder: (workOrderId: string) =>
    apiClient.get(`/quality/iirs/work-order/${workOrderId}`),

  assignInspector: (id: string, inspectorId: string) =>
    apiClient.post(`/quality/iirs/${id}/assign-inspector`, { inspectorId }),

  updateInspectionDetails: (id: string, data: {
    inspection_date?: string;
    findings?: string;
    observations?: string;
    supporting_documents?: string[];
    photos?: string[];
    report_date?: string | null;
    report_title?: string | null;
    report_subtitle?: string | null;
    report_no?: string | null;
    job_no?: string | null;
    wr_no?: string | null;
    client_ref_1?: string | null;
    client_ref_2?: string | null;
    po_no?: string | null;
    make?: string | null;
    gate_pass_no?: string | null;
    qty_no?: string | null;
    customer_name?: string | null;
    work_description?: string | null;
    equipment_name?: string | null;
    scope_of_work_text?: string | null;
    remarks?: string | null;
    findings_rows?: InitialInspectionFindingsRow[] | null;
    parts_replace_rows?: InitialInspectionPartLineRow[] | null;
    parts_repair_rows?: InitialInspectionPartLineRow[] | null;
    prepared_by_name?: string | null;
    prepared_by_print_name?: string | null;
    prepared_by_designation?: string | null;
    approved_by_name?: string | null;
    approved_by_print_name?: string | null;
    approved_by_designation?: string | null;
  }) =>
    apiClient.put(`/quality/iirs/${id}/inspection-details`, data),

  approve: (id: string) =>
    apiClient.post(`/quality/iirs/${id}/approve`),

  reject: (id: string, rejectionReason: string) =>
    apiClient.post(`/quality/iirs/${id}/reject`, { rejectionReason }),

  cancel: (id: string) =>
    apiClient.post(`/quality/iirs/${id}/cancel`),

  checkWorkOrderBlocked: (workOrderId: string) =>
    apiClient.get(`/quality/iirs/work-order/${workOrderId}/check-blocked`),
};

// =====================================================
// STAGE COMPLETION INSPECTION (SCI) SERVICE
// =====================================================
export const sciService = {
  createForStage: (stageId: string) =>
    apiClient.post(`/quality/stage-inspections/stage/${stageId}`),

  getAll: (filters?: { work_order_id?: string; status?: string }) => {
    const params = new URLSearchParams();
    if (filters?.work_order_id) params.append('work_order_id', filters.work_order_id);
    if (filters?.status) params.append('status', filters.status);
    return apiClient.get(`/quality/stage-inspections?${params.toString()}`);
  },

  getById: (id: string) =>
    apiClient.get(`/quality/stage-inspections/${id}`),

  getByStage: (stageId: string) =>
    apiClient.get(`/quality/stage-inspections/stage/${stageId}`),

  getLatestSummaryByStage: (stageId: string) =>
    apiClient.get(`/quality/stage-inspections/stage/${stageId}/latest-summary`),

  getByWorkOrder: (workOrderId: string) =>
    apiClient.get(`/quality/stage-inspections/work-order/${workOrderId}`),

  startInspection: (id: string) =>
    apiClient.post(`/quality/stage-inspections/${id}/start`),

  recordMeasurements: (id: string, measurements: Array<{
    stage_dimension_id: string;
    unit_sequence: number;
    measured_value?: number | null;
    actual_text?: string | null;
    notes?: string;
    measured_by?: string;
  }>) =>
    apiClient.post(`/quality/stage-inspections/${id}/measurements`, { measurements }),

  completeInspection: (id: string, data: {
    findings?: string;
    observations?: string;
    supporting_documents?: string[];
    photos?: string[];
  }) =>
    apiClient.post(`/quality/stage-inspections/${id}/complete`, data),

  getMeasurements: (id: string) =>
    apiClient.get(`/quality/stage-inspections/${id}/measurements`),

  updateReportMetadata: (id: string, report_metadata: Record<string, unknown>, tmmde_override_reason?: string) =>
    apiClient.patch(`/quality/stage-inspections/${id}/report-metadata`, {
      report_metadata,
      ...(tmmde_override_reason ? { tmmde_override_reason } : {}),
    }),

  clone: (id: string) =>
    apiClient.post(`/quality/stage-inspections/${id}/clone`),

  createRevision: (id: string) =>
    apiClient.post(`/quality/stage-inspections/${id}/revision`),

  delete: (id: string) =>
    apiClient.delete(`/quality/stage-inspections/${id}`),

  updateMeasurement: (measurementId: string, data: {
    measured_value?: number | null;
    actual_text?: string | null;
    notes?: string;
  }) =>
    apiClient.put(`/quality/stage-inspections/measurements/${measurementId}`, data),

  // Checkpoints
  getCheckpoints: (inspectionId: string) =>
    apiClient.get(`/quality/stage-inspections/${inspectionId}/checkpoints`),

  addCheckpoint: (inspectionId: string, data: {
    checkpoint_type: string;
    description?: string;
    acceptance_criteria?: string;
    result?: string;
    remarks?: string;
    sequence_number?: number;
  }) =>
    apiClient.post(`/quality/stage-inspections/${inspectionId}/checkpoints`, data),

  updateCheckpoint: (checkpointId: string, data: {
    checkpoint_type?: string;
    description?: string;
    acceptance_criteria?: string;
    result?: string;
    remarks?: string;
    sequence_number?: number;
  }) =>
    apiClient.put(`/quality/stage-inspections/checkpoints/${checkpointId}`, data),

  deleteCheckpoint: (checkpointId: string) =>
    apiClient.delete(`/quality/stage-inspections/checkpoints/${checkpointId}`),

  // Attachments
  getAttachments: (inspectionId: string) =>
    apiClient.get(`/quality/stage-inspections/${inspectionId}/attachments`),

  uploadAttachment: (inspectionId: string, file: File, category?: string, remarks?: string) => {
    const formData = new FormData();
    formData.append('file', file);
    if (category) formData.append('category', category);
    if (remarks) formData.append('remarks', remarks);
    return apiClient.post(`/quality/stage-inspections/${inspectionId}/attachments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  deleteAttachment: (attachmentId: string) =>
    apiClient.delete(`/quality/stage-inspections/attachments/${attachmentId}`),

  // Hold-point & Witness
  releaseHoldPoint: (inspectionId: string, data: { notes?: string }) =>
    apiClient.post(`/quality/stage-inspections/${inspectionId}/release-hold`, data),

  completeWitnessPoint: (inspectionId: string, data: {
    witness_name: string;
    witness_party: string;
    witness_signature?: any;
  }) =>
    apiClient.post(`/quality/stage-inspections/${inspectionId}/complete-witness`, data),
};

// =====================================================
// FINAL INSPECTION REQUEST (FIR) SERVICE
// =====================================================
export const firService = {
  createForWorkOrder: (workOrderId: string) =>
    apiClient.post(`/quality/final-inspection-reports`, { work_order_id: workOrderId }),

  getAll: (filters?: { status?: string; workOrderId?: string; approval_status?: string }) => {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.approval_status) params.append('approval_status', filters.approval_status);
    return apiClient.get(`/quality/final-inspection-reports?${params.toString()}`);
  },

  getById: (id: string) =>
    apiClient.get(`/quality/final-inspection-reports/${id}`),

  getByWorkOrder: (workOrderId: string) =>
    apiClient.get(`/quality/final-inspection-reports/work-order/${workOrderId}`),

  assignInspector: (id: string, inspectorId: string) =>
    apiClient.post(`/quality/final-inspection-reports/${id}/assign-inspector`, { inspectorId }),

  recordLineItemResults: (id: string, results: Array<{
    line_item_id: string;
    approved_quantity: number;
    rejected_quantity: number;
    rejection_reason?: string;
  }>) =>
    apiClient.post(`/quality/final-inspection-reports/${id}/line-item-results`, { results }),

  approve: (id: string, approvalData?: { approval_comments?: string; approval_documents?: string[] }) =>
    apiClient.post(`/quality/final-inspection-reports/${id}/approve`, {
      approval_status: 'Approved',
      ...approvalData
    }),

  reject: (id: string, rejectionReason: string, comments?: string) =>
    apiClient.post(`/quality/final-inspection-reports/${id}/approve`, {
      approval_status: 'Rejected',
      rejection_reason: rejectionReason,
      approval_comments: comments
    }),

  delete: (id: string) =>
    apiClient.delete(`/quality/final-inspection-reports/${id}`),
};

// Final Inspection Request (final_inspection_requests – used by IRN; created when WO is completed)
export const firRequestService = {
  getAll: async (filters?: { status?: string; workOrderId?: string }): Promise<FinalInspectionRequest[]> => {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.workOrderId) params.append('workOrderId', filters.workOrderId);
    const qs = params.toString();
    const res = await apiClient.get<FinalInspectionRequest[]>(
      `/quality/final-inspection-requests${qs ? `?${qs}` : ''}`
    );
    const raw = (res as { data?: FinalInspectionRequest[] })?.data ?? res;
    return Array.isArray(raw) ? raw : [];
  },
  getByWorkOrder: (workOrderId: string) =>
    apiClient.get(`/quality/final-inspection-requests/work-order/${workOrderId}`),
  getById: async (id: string): Promise<FinalInspectionRequest> => {
    const res = await apiClient.get<FinalInspectionRequest>(`/quality/final-inspection-requests/${id}`);
    const raw = (res as { data?: FinalInspectionRequest })?.data ?? res;
    return raw as FinalInspectionRequest;
  },
  assignInspector: (id: string, inspectorId: string) =>
    apiClient.post(`/quality/final-inspection-requests/${id}/assign-inspector`, { inspectorId }),
  recordLineItemResults: (
    id: string,
    results: Array<{
      line_item_id: string;
      approved_quantity: number;
      rejected_quantity: number;
      rejection_reason?: string;
    }>
  ) => apiClient.put(`/quality/final-inspection-requests/${id}/line-item-results`, { results }),
  addAttachment: (id: string, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post(`/quality/final-inspection-requests/${id}/attachments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteAttachment: (attachmentId: string) =>
    apiClient.delete(`/quality/final-inspection-requests/attachments/${attachmentId}`),
  createForWorkOrder: (workOrderId: string) =>
    apiClient.post(`/quality/final-inspection-requests/work-order/${workOrderId}`, {}),
  approve: (id: string) =>
    apiClient.post(`/quality/final-inspection-requests/${id}/approve`, {}),
  reject: (id: string, rejectionReason: string) =>
    apiClient.post(`/quality/final-inspection-requests/${id}/reject`, { rejectionReason }),
  updateInspectionDetails: (
    id: string,
    body: { inspection_criteria?: string | null; equipment_asset_ids?: string[]; tmmde_override_reason?: string }
  ) =>
    apiClient.put(`/quality/final-inspection-requests/${id}/inspection-details`, body),
  syncFromReport: (workOrderId: string) =>
    apiClient.post(`/quality/final-inspection-requests/work-order/${workOrderId}/sync-from-report`, {}),
};

// =====================================================
// INSPECTION RELEASE NOTE (IRN) SERVICE
// =====================================================
export const irnService = {
  createForFIR: (firId: string) =>
    apiClient.post(`/quality/irns/fir/${firId}`),

  createForDeliveryNote: (deliveryNoteId: string) =>
    apiClient.post(`/quality/irns/delivery-note/${deliveryNoteId}`, {}),

  getByDeliveryNote: (deliveryNoteId: string) =>
    apiClient.get(`/quality/irns/delivery-note/${deliveryNoteId}`),

  getAll: (filters?: { status?: string; workOrderId?: string; deliveryNoteId?: string }) => {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.workOrderId) params.append('workOrderId', filters.workOrderId);
    if (filters?.deliveryNoteId) params.append('deliveryNoteId', filters.deliveryNoteId);
    return apiClient.get(`/quality/irns?${params.toString()}`);
  },

  getById: (id: string) =>
    apiClient.get(`/quality/irns/${id}`),

  getByWorkOrder: (workOrderId: string) =>
    apiClient.get(`/quality/irns/work-order/${workOrderId}`),

  getByFIR: (firId: string) =>
    apiClient.get(`/quality/irns/fir/${firId}`),

  approve: (id: string, notes?: string) =>
    apiClient.post(`/quality/irns/${id}/approve`, { notes }),

  reject: (id: string, rejectionReason: string) =>
    apiClient.post(`/quality/irns/${id}/reject`, { rejectionReason }),

  cancel: (id: string) =>
    apiClient.post(`/quality/irns/${id}/cancel`),

  checkCanPrepareDispatch: (workOrderId: string) =>
    apiClient.get(`/quality/irns/work-order/${workOrderId}/can-prepare-dispatch`),
};

// =====================================================
// WORKSTATION & SHIFT SERVICES
// =====================================================
/** Same company scope as `useManufacturing` — required by manufacturing list APIs. */
export function getQcContextCompanyId(): string {
  const userStr = localStorage.getItem('user');
  if (!userStr) return '';
  try {
    const user = JSON.parse(userStr) as { company_id?: string };
    return user.company_id || '';
  } catch {
    return '';
  }
}

export interface Workstation {
  id: string;
  workstation_code: string;
  workstation_name: string;
  description?: string;
  is_active: boolean;
}

export interface Shift {
  id: string;
  shift_name: string;
  shift_code: string;
  start_time: string;
  end_time: string;
  is_active: boolean;
}

export const workstationService = {
  getAll: () => {
    const company_id = getQcContextCompanyId();
    const params = new URLSearchParams();
    if (company_id) params.set('company_id', company_id);
    return apiClient.get(`/manufacturing/workstations?${params.toString()}`);
  },
  /** Fetches by company; caller filters to `is_active` if needed. */
  getActive: () => workstationService.getAll(),
};

export const shiftService = {
  getAll: () => {
    const company_id = getQcContextCompanyId();
    const params = new URLSearchParams();
    if (company_id) params.set('company_id', company_id);
    return apiClient.get(`/manufacturing/shifts?${params.toString()}`);
  },
  getActive: () => {
    const company_id = getQcContextCompanyId();
    const params = new URLSearchParams();
    if (company_id) params.set('company_id', company_id);
    return apiClient.get(`/manufacturing/shifts/active?${params.toString()}`);
  },
};
